/**
 * Écran de connexion.
 *
 * Construit en JS et non dans index.html : il doit pouvoir s'afficher avant
 * que quoi que ce soit d'autre n'existe, y compris quand Supabase n'est pas
 * configuré du tout.
 */

import { isAllowed, sendMagicLink } from '../supabase/auth.js'
import { escapeHtml } from './dom.js'

function render(html) {
  const gate = document.createElement('div')
  gate.className = 'gate'
  gate.id = 'gate'
  gate.innerHTML = `<div class="gate-card">${html}</div>`
  document.body.appendChild(gate)
  return gate
}

export function removeGate() {
  document.getElementById('gate')?.remove()
}

/** Erreur de configuration : sans clés, aucun formulaire n'aurait de sens. */
export function showConfigError(message) {
  removeGate()
  render(`
    <h1>Configuration incomplète</h1>
    <p>${escapeHtml(message)}</p>
    <div class="gate-foot">Voir le README, section « Mise en route ».</div>
  `)
}

/** Compte authentifié mais absent de la liste blanche. */
export function showNotAllowed(email, onSignOut) {
  removeGate()
  const gate = render(`
    <h1>Accès non autorisé</h1>
    <p>
      L'adresse <strong>${escapeHtml(email)}</strong> n'est pas sur la liste des personnes
      autorisées. Demande à Kamel de l'ajouter, puis reconnecte-toi.
    </p>
    <button id="gateSignOut">Utiliser une autre adresse</button>
  `)
  gate.querySelector('#gateSignOut').addEventListener('click', onSignOut)
}

/**
 * Formulaire de connexion. La session s'ouvre via onAuthChange, pas ici.
 *
 * @param initialError message d'un lien magique en échec, à afficher d'emblée :
 *   sans lui, l'utilisateur revoit le formulaire sans savoir pourquoi.
 */
export function showLogin(initialError = null) {
  // Supabase émet INITIAL_SESSION dès l'abonnement à onAuthChange, ce qui
  // rappelle cette fonction juste après le premier rendu. Reconstruire
  // l'écran effacerait le message qui vient d'y être posé — et, plus
  // largement, tout ce que l'utilisateur y aurait déjà tapé.
  const existing = document.querySelector('#gate #gateForm')
  if (existing) {
    if (initialError) {
      const msg = document.querySelector('#gateMsg')
      msg.className = 'gate-msg err'
      msg.textContent = initialError
    }
    return
  }

  removeGate()
  const gate = render(`
    <h1>GMF &amp; hôpitaux du Québec</h1>
    <p>Entre ton adresse professionnelle : tu recevras un lien de connexion, sans mot de passe.</p>
    <form id="gateForm">
      <input id="gateEmail" type="email" required autocomplete="email" placeholder="prenom@exemple.com" />
      <button type="submit" id="gateBtn">Recevoir le lien</button>
    </form>
    <div class="gate-msg${initialError ? ' err' : ''}" id="gateMsg">${initialError ? escapeHtml(initialError) : ''}</div>
    <div class="gate-foot">Seules les adresses autorisées peuvent accéder aux données.</div>
  `)

  const form = gate.querySelector('#gateForm')
  const input = gate.querySelector('#gateEmail')
  const btn = gate.querySelector('#gateBtn')
  const msg = gate.querySelector('#gateMsg')

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    const email = input.value.trim()
    if (!email) return

    btn.disabled = true
    btn.textContent = 'Envoi…'
    msg.className = 'gate-msg'
    msg.textContent = ''

    try {
      await sendMagicLink(email)
      msg.className = 'gate-msg ok'
      msg.textContent = `Lien envoyé à ${email}. Ouvre-le depuis ce navigateur.`
      btn.textContent = 'Lien envoyé'
      // Bouton laissé désactivé : le renvoi immédiat est limité côté Supabase,
      // et un second clic ne ferait qu'afficher une erreur de quota.
    } catch (err) {
      msg.className = 'gate-msg err'
      msg.textContent = err.message || "Envoi impossible. Vérifie l'adresse."
      btn.disabled = false
      btn.textContent = 'Recevoir le lien'
    }
  })

  input.focus()
}

/**
 * Aiguille selon la session.
 * @returns {Promise<boolean>} true si l'app peut démarrer
 */
export async function guard(session, onSignOut, authError = null) {
  if (!session) {
    showLogin(authError)
    return false
  }
  if (!(await isAllowed())) {
    showNotAllowed(session.user.email, onSignOut)
    return false
  }
  removeGate()
  return true
}
